import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Modal,
  Button,
  Container,
  Row,
  Col,
  Card,
  Form,
  Table,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import {
  BsListCheck,
  BsArrowClockwise,
  BsSearch,
  BsFileEarmarkExcel,
} from "react-icons/bs";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import { FaHistory } from "react-icons/fa";
import { toast } from "react-toastify";

function Fundmanagement() {
  const [fundSources, setFundSources] = useState([]);
  const [fundHistory, setFundHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedFundForHistory, setSelectedFundForHistory] = useState(null);
  const [singleFundHistory, setSingleFundHistory] = useState([]);

  // Filter State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [transactionType, setTransactionType] = useState("all");

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;

  const fetchFundSources = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/fundlistwithoutbarrerton.php`
      );
      if (response.data.success) {
        const formattedFunds = response.data.funds.map((fund) => ({
          id: parseInt(fund.id, 10),
          name: fund.fund_name,
          total: parseFloat(fund.amount) || 0,
        }));
        setFundSources(formattedFunds);
      } else {
        toast.error(`Error fetching funds: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while fetching the fund sources.");
    }
  };

  const fetchFundHistory = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/fundhistorywithouttoken.php`
      );
      if (response.data.success) {
        // console.log(response)

        const formattedHistory = response.data.fund_historys.map((entry) => ({
          id: parseInt(entry.id, 10),
          name: entry.fund_name,
          amount: parseFloat(entry.amount),
          date: entry.date,
          go_number: entry.go_number,
          purpose: entry.purpose,
          work_order: entry.work_order || null,
          type: entry.work_order ? "debit" : "credit",
        }));
        setFundHistory(formattedHistory);
      } else {
        toast.error(`Error fetching fund history: ${response.data.message}`);
      }
    } catch (error) {
      toast.error("An error occurred while fetching the fund history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFundSources();
    fetchFundHistory();
  }, []);

  const handleShowHistoryModal = async (fund) => {
    setSelectedFundForHistory(fund);
    try {
      const response = await axios.post(
        `${API_URL}/singlefundhistorywithouttoken.php`,
        { fund_id: fund.id.toString() }
      );
      if (response.data.success) {
        setSingleFundHistory(response.data.history || []);
      } else {
        setSingleFundHistory([]);
      }
    } catch (error) {
      setSingleFundHistory([]);
    }
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedFundForHistory(null);
    setSingleFundHistory([]);
  };

  const filteredFundHistory = useMemo(() => {
    return fundHistory.filter((item) => {
      const itemDate = new Date(item.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;
      
      if (transactionType !== "all" && item.type !== transactionType) return false;

      const search = searchTerm.toLowerCase();
      if (
        search &&
        !item.name.toLowerCase().includes(search) &&
        !(item.work_order && item.work_order.toLowerCase().includes(search)) &&
        !(item.go_number && item.go_number.toLowerCase().includes(search)) &&
        !(item.purpose && item.purpose.toLowerCase().includes(search))
      ) {
        return false;
      }
      return true;
    }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort in descending order by date
  }, [fundHistory, startDate, endDate, searchTerm, transactionType]);

  const { totalCredit, totalDebit } = useMemo(() => {
    return filteredFundHistory.reduce(
      (acc, item) => {
        if (item.type === "credit") acc.totalCredit += item.amount;
        else acc.totalDebit += item.amount;
        return acc;
      },
      { totalCredit: 0, totalDebit: 0 }
    );
  }, [filteredFundHistory]);

  const handleExport = () => {
    const dataToExport = filteredFundHistory.map((item) => ({
      "Fund Source": item.name,
      "Type": item.type,
      "Amount Added (₹)": item.amount,
      "Date": item.date,
      "G.O Number": item.go_number || "N/A",
      "Purpose": item.purpose || "N/A",
      "Work Order": item.work_order || "NIL",
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FundHistory");
    XLSX.writeFile(workbook, "Fund_History_Report.xlsx");
  };

  const historyColumns = [
    {
      name: "Fund Source",
      selector: (row) => row.name,
      sortable: true,
      width: "15%",
      minWidth: "120px",
    },
    {
        name: "Type",
        selector: (row) => row.type,
        sortable: true,
        width: "8%",
        minWidth: "80px",
        cell: (row) => (
            <span
            className={`badge bg-${
                row.type === "credit" ? "success" : "danger"
            }`}
            >
            {row.type.charAt(0).toUpperCase() + row.type.slice(1)}
            </span>
        ),
    },
    {
      name: "Amount (₹)",
      selector: (row) => row.amount,
      sortable: true,
      width: "12%",
      minWidth: "100px",
      right: true,
      cell: (row) => row.amount.toLocaleString("en-IN"),
    },
    { 
      name: "Date", 
      selector: (row) => row.date, 
      sortable: true,
      width: "12%",
      minWidth: "100px",
    },
    { 
      name: "G.O Number", 
      selector: (row) => row.go_number || "N/A", 
      sortable: true,
      width: "12%",
      minWidth: "100px",
    },
    {
      name: "Purpose",
      selector: (row) => row.purpose,
      sortable: true,
      width: "25%",
      minWidth: "150px",
      cell: (row) => (
        <div title={row.purpose}>
          {row.purpose && row.purpose.length > 30
            ? `${row.purpose.substring(0, 30)}...`
            : row.purpose || "N/A"}
        </div>
      ),
    },
    {
      name: "Work Order",
      selector: (row) => row.work_order || "NIL",
      sortable: true,
      width: "16%",
      minWidth: "120px",
    },
  ];

  const customStyles = {
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "50px",
      },
    },
    headCells: {
      style: {
        fontSize: "15px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
        padding: "12px 8px",
        textAlign: "left",
      },
    },
    cells: {
      style: {
        padding: "12px 8px",
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        minWidth: "100%",
      },
    },
  };

  return (
    <>
      <Topbar />
      <div className="d-flex">
        <Sidebar />
        <Container fluid className="p-4 bg-light">
          <header className="mb-4">
            <h1 className="h3 d-flex align-items-center gap-2">
              Fund Management
            </h1>
          </header>

          <Row className="g-4 mb-4">
            <Col lg={12}>
              <Card className="h-100 shadow-sm">
                <Card.Header
                  as="h5"
                  className="d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#e3f2fd",
                    borderBottom: "2px solid #1976d2",
                  }}
                >
                  <BsListCheck style={{ color: "#1976d2" }} />{" "}
                  <span style={{ color: "#1976d2" }}>
                    Available Fund Sources
                  </span>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover responsive="sm">
                      <thead
                        className="table-dark"
                        style={{ backgroundColor: "#0d6efd" }}
                      >
                        <tr>
                          <th>Fund Name</th>
                          <th>Total Amount (₹)</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fundSources.map((fund) => (
                          <tr key={fund.id}>
                            <td>{fund.name}</td>
                            <td>{fund.total.toLocaleString()}</td>
                            <td className="d-flex gap-2">
                              <Button
                                variant="info"
                                size="sm"
                                onClick={() => handleShowHistoryModal(fund)}
                              >
                                <FaHistory />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm">
            <Card.Header
              as="h5"
              className="d-flex align-items-center gap-2"
              style={{
                backgroundColor: "#e3f2fd",
                borderBottom: "2px solid #1976d2",
              }}
            >
              <BsArrowClockwise style={{ color: "#1976d2" }} />{" "}
              <span style={{ color: "#1976d2" }}>Fund Additions History</span>
            </Card.Header>
            <Card.Body>
              <div className="p-3 border-bottom bg-light rounded mb-4">
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label>Search</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <BsSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                    </Form.Select>
                  </Col>
                  <Col md={1} className="text-end">
                    <Button onClick={handleExport} variant="success">
                      <BsFileEarmarkExcel />
                    </Button>
                  </Col>
                </Row>
              </div>

              <Row className="px-3 text-center g-3 mb-4">
                <Col md={4}>
                  <Card className="shadow-sm border-success">
                    <Card.Body>
                      <Card.Title className="text-success">Total Credit</Card.Title>
                      <Card.Text className="fs-4 fw-bold text-success">
                        ₹{totalCredit.toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="shadow-sm border-danger">
                    <Card.Body>
                      <Card.Title className="text-danger">Total Debit</Card.Title>
                      <Card.Text className="fs-4 fw-bold text-danger">
                        ₹{totalDebit.toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="shadow-sm border-primary">
                    <Card.Body>
                      <Card.Title className="text-primary">Net Balance</Card.Title>
                      <Card.Text className="fs-4 fw-bold text-primary">
                        ₹{(totalCredit - totalDebit).toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <DataTable
                columns={historyColumns}
                data={filteredFundHistory}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                responsive
                highlightOnHover
                progressPending={loading}
                progressComponent={
                  <Spinner animation="border" variant="primary" />
                }
                customStyles={customStyles}
              />
            </Card.Body>
          </Card>
        </Container>
      </div>

      <Modal
        show={showHistoryModal}
        onHide={handleCloseHistoryModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <FaHistory />
              <span>Fund History</span>
            </div>
            <small
              className="text-muted d-block mt-1"
              style={{ wordBreak: "break-word" }}
            >
              {selectedFundForHistory?.name}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive" style={{ maxHeight: "400px" }}>
            <Table striped bordered hover responsive="sm">
              <thead className="table-dark">
                <tr>
                  <th>Amount Added (₹)</th>
                  <th>Date</th>
                  <th>G.O Number</th>
                  <th>Purpose</th>
                  <th>Work Order</th>
                </tr>
              </thead>
              <tbody>
                {singleFundHistory.length > 0 ? (
                  singleFundHistory.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.amount.toLocaleString()}</td>
                      <td>{entry.date}</td>
                      <td>{entry.go_number || "N/A"}</td>
                      <td title={entry.purpose}>
                        {entry.purpose && entry.purpose.length > 30
                          ? `${entry.purpose.substring(0, 30)}...`
                          : entry.purpose || "N/A"}
                      </td>
                      <td>{entry.work_order || "NIL"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No history found for this fund.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseHistoryModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Fundmanagement;
